import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  FiFileText, FiPlus, FiDownload, FiTrash2, FiEye,
  FiCalendar, FiUser, FiPaperclip, FiX, FiCheck,
  FiSearch, FiFilter, FiChevronDown
} from "react-icons/fi";
import toast from "react-hot-toast";
import FieldToast from "../components/FieldToast";
import { getAllReports, createReport, deleteReport } from "../api/api";
import Pagination from "../components/Pagination";
import "./Reports.css";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function Reports() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const userName = currentUser?.userName || currentUser?.email?.split("@")[0] || "Manager";
  const currentYear = new Date().getFullYear();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [viewReport, setViewReport] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMonth, setFilterMonth] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [form, setForm] = useState({
    title: "",
    month: MONTHS[new Date().getMonth()],
    year: currentYear,
    content: "",
    attachments: [],
    author: userName,
  });

  // Field refs for validation toasts
  const titleRef = useRef(null);
  const contentRef = useRef(null);
  const monthRef = useRef(null);

  // Field toast state
  const [fieldToast, setFieldToast] = useState({
    show: false,
    message: "",
    targetRef: null,
  });

  const showFieldToast = (message, targetRef) => {
    setFieldToast({
      show: true,
      message,
      targetRef,
    });
    setTimeout(() => {
      setFieldToast((prev) => ({ ...prev, show: false }));
    }, 3000);
  };

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const res = await getAllReports();
      const list = res?.data?.data ?? res?.data ?? [];
      setReports(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    // Title validation
    if (!form.title || (typeof form.title === 'string' && form.title.trim() === "")) {
      showFieldToast("Report title is required", titleRef);
      return;
    }

    if (typeof form.title === 'string' && form.title.trim().length < 3) {
      showFieldToast("Report title must be at least 3 characters", titleRef);
      return;
    }

    if (typeof form.title === 'string' && form.title.trim().length > 100) {
      showFieldToast("Report title must be less than 100 characters", titleRef);
      return;
    }

    // Content validation
    if (!form.content || (typeof form.content === 'string' && form.content.trim() === "")) {
      showFieldToast("Report content is required", contentRef);
      return;
    }

    if (typeof form.content === 'string' && form.content.trim().length < 10) {
      showFieldToast("Report content must be at least 10 characters", contentRef);
      return;
    }

    // Month validation
    if (!form.month) {
      showFieldToast("Month is required", monthRef);
      return;
    }

    // Year validation
    if (!form.year || form.year < 2000 || form.year > 2100) {
      showFieldToast("Please enter a valid year (2000-2100)", monthRef);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("title", typeof form.title === 'string' ? form.title.trim() : String(form.title || ""));
      formData.append("month", form.month);
      formData.append("year", form.year);
      formData.append("content", typeof form.content === 'string' ? form.content.trim() : String(form.content || ""));
      formData.append("author", form.author || userName);
      form.attachments.forEach((att) => {
        if (att.file) formData.append("attachments", att.file);
      });

      const res = await createReport(formData);
      const newReport = res?.data ?? res;
      setReports((prev) => [newReport, ...prev]);
      setShowCreate(false);
      setForm({
        title: "",
        month: MONTHS[new Date().getMonth()],
        year: currentYear,
        content: "",
        attachments: [],
        author: userName,
      });
      toast.success("Report submitted successfully!");
    } catch (err) {
      toast.error("Failed to submit report");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this report?")) return;
    try {
      await deleteReport(id);
      setReports((prev) => prev.filter((r) => (r._id ?? r.id) !== id));
      toast.success("Report deleted");
    } catch (err) {
      toast.error("Failed to delete report");
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map((f) => ({
      name: f.name,
      size: (f.size / (1024 * 1024)).toFixed(1) + " MB",
      file: f,
    }));
    setForm((prev) => ({ ...prev, attachments: [...prev.attachments, ...newAttachments] }));
  };

  const removeAttachment = (idx) => {
    setForm((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== idx),
    }));
  };

  const handleDownload = async (attachment) => {
    try {
      const token = localStorage.getItem("token");
      let downloadUrl = attachment.url;

      if (!downloadUrl && attachment.file) {
        const url = URL.createObjectURL(attachment.file);
        const a = document.createElement("a");
        a.href = url;
        a.download = attachment.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return;
      }

      if (downloadUrl) {
        const res = await fetch(downloadUrl, {
          headers: token ? { Authorization: token } : {},
        });
        if (!res.ok) throw new Error("Failed to download file");

        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = attachment.name || "download";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        toast.error("File not available for download");
      }
    } catch (err) {
      toast.error("Failed to download file");
    }
  };

  // Extract month from createdAt if month field is missing
  const reportsWithMonth = reports.map(r => {
    if (!r.month && r.createdAt) {
      const date = new Date(r.createdAt);
      r.month = MONTHS[date.getMonth()];
    }
    return r;
  });

  const filteredReports = reportsWithMonth.filter((r) => {
    const matchesSearch =
      (r.title && r.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (r.month && r.month.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesMonth = filterMonth === "all" || (r.month && r.month.toLowerCase() === filterMonth.toLowerCase());
    return matchesSearch && matchesMonth;
  });

  // Pagination
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const paginatedReports = filteredReports.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterMonth]);

  return (
    <div className="reports-page">
      {/* Header */}
      <div className="reports-header">
        <div>
          <h1>Monthly Reports</h1>
          <p className="reports-subtitle">Create and manage monthly team reports</p>
        </div>
        <div className="reports-header-actions">
          <button className="deleted-reports-btn" onClick={() => navigate("/reports/deleted")}>
            <FiTrash2 size={18} />
            Deleted Reports
          </button>
          <button className="create-report-btn" onClick={() => setShowCreate(true)}>
            <FiPlus size={18} />
            New Report
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="reports-filters">
        <div className="filter-search">
          <FiSearch size={16} />
          <input
            type="text"
            placeholder="Search reports..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <div className="filter-select-wrapper">
            <FiCalendar size={14} />
            <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
              <option value="all">All Months</option>
              {MONTHS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
         
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="reports-stats">
        <div className="reports-stat-card">
          <div className="reports-stat-icon" style={{ background: "rgba(0, 168, 132, 0.15)", color: "#00a884" }}>
            <FiFileText size={20} />
          </div>
          <div>
            <span className="reports-stat-value">{reports.length}</span>
            <span className="reports-stat-label">Total Reports</span>
          </div>
        </div>
        <div className="reports-stat-card">
          <div className="reports-stat-icon" style={{ background: "rgba(59, 130, 246, 0.15)", color: "#3b82f6" }}>
            <FiCheck size={20} />
          </div>
          <div>
            <span className="reports-stat-value">{reports.filter((r) => r.status === "submitted").length}</span>
            <span className="reports-stat-label">Submitted</span>
          </div>
        </div>
        <div className="reports-stat-card">
          <div className="reports-stat-icon" style={{ background: "rgba(245, 158, 11, 0.15)", color: "#f59e0b" }}>
            <FiFileText size={20} />
          </div>
          <div>
            <span className="reports-stat-value">{reports.filter((r) => r.status === "draft").length}</span>
            <span className="reports-stat-label">Drafts</span>
          </div>
        </div>
      </div>

      {/* Reports Table */}
      {loading ? (
        <div className="reports-loading">Loading reports...</div>
      ) : filteredReports.length === 0 ? (
        <div className="reports-empty">
          <FiFileText size={48} />
          <h3>No reports found</h3>
          <p>Create your first monthly report to get started</p>
        </div>
      ) : (
        <div className="reports-table-wrapper">
          <div className="reports-table-scroll">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Report</th>
                  <th>Author & Month</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Files</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.length === 0 ? (
                  <tr><td colSpan={6}><div className="reports-empty"><h3>No reports found</h3><p>Create your first monthly report to get started</p></div></td></tr>
                ) : (
                  paginatedReports.map((report) => (
                    <tr key={report._id ?? report.id}>
                    <td>
                      <div className="report-title-cell">
                        <FiFileText size={18} color="#00a884" />
                        <span className="report-title-text">{report.title}</span>
                      </div>
                    </td>
                    <td>
                      <div className="report-author-month">
                        <div className="report-author">
                          <FiUser size={14} />
                          {report.author || report.createdBy?.userName || report.createdBy?.name || "Unknown"}
                        </div>
                        <div className="report-month-badge">
                          <FiCalendar size={12} />
                          {report.month}
                        </div>
                      </div>
                    </td>
                    <td>{report.createdAt ? new Date(report.createdAt).toLocaleDateString() : "—"}</td>
                    <td>
                      <span className={`report-status-badge ${report.status}`}>
                        {report.status}
                      </span>
                    </td>
                    <td>
                      {report.attachments?.length > 0 ? (
                        <span className="report-files-count">
                          <FiPaperclip size={14} />
                          {report.attachments.length}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <div className="report-actions">
                        <button
                          className="report-action-btn view"
                          onClick={() => setViewReport(report)}
                          title="View"
                        >
                          <FiEye size={16} />
                        </button>
                        <button
                          className="report-action-btn download"
                          onClick={() => {
                            if (report.attachments?.length > 0) {
                              handleDownload(report.attachments[0]);
                            } else {
                              toast.error("No attachments to download");
                            }
                          }}
                          title="Download"
                        >
                          <FiDownload size={16} />
                        </button>
                        <button
                          className="report-action-btn delete"
                          onClick={() => handleDelete(report._id ?? report.id)}
                          title="Delete"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onChange={setCurrentPage}
      />

      {/* Create Report Modal */}
      {showCreate && (
        <div className="report-modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="report-modal-header">
              <h2><FiPlus size={20} /> Create Monthly Report</h2>
              <button className="report-modal-close" onClick={() => setShowCreate(false)}>
                <FiX size={20} />
              </button>
            </div>

            <div className="report-modal-body">
              <div className="report-form-row">
                <div className="report-form-group flex-2">
                  <label>Report Title</label>
                  <input
                    ref={titleRef}
                    type="text"
                    placeholder="e.g. April 2025 Monthly Report"
                    value={form.title}
                    onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                <div className="report-form-group">
                  <label>Month</label>
                  <select
                    ref={monthRef}
                    value={form.month}
                    onChange={(e) => setForm((prev) => ({ ...prev, month: e.target.value, year: currentYear }))}
                  >
                    {MONTHS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="report-form-group">
                <label>Report Content</label>
                <textarea
                  ref={contentRef}
                  rows={10}
                  placeholder="Write your monthly report here...&#10;&#10;- Key achievements&#10;- Challenges faced&#10;- Goals for next month&#10;- Financial summary&#10;- Team updates"
                  value={form.content}
                  onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
                />
              </div>

              <div className="report-form-group">
                <label>Attachments</label>
                <div className="report-attachment-upload">
                  <FiPaperclip size={20} />
                  <p>Drag & drop files here or click to browse</p>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="report-file-input"
                  />
                </div>
                {form.attachments.length > 0 && (
                  <div className="report-attachments-list">
                    {form.attachments.map((att, idx) => (
                      <div key={idx} className="report-attachment-item">
                        <FiFileText size={14} />
                        <span>{att.name}</span>
                        <span className="report-attachment-size">{att.size}</span>
                        <button onClick={() => removeAttachment(idx)}>
                          <FiX size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="report-modal-footer">
              <button className="report-btn-secondary" onClick={() => setShowCreate(false)}>
                Cancel
              </button>
              <button
                className="report-btn-primary"
                onClick={handleCreate}
              >
                <FiCheck size={16} />
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Report Modal */}
      {viewReport && (
        <div className="report-modal-overlay" onClick={() => setViewReport(null)}>
          <div className="report-modal report-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="report-modal-header">
              <h2><FiFileText size={20} /> {viewReport.title}</h2>
              <button className="report-modal-close" onClick={() => setViewReport(null)}>
                <FiX size={20} />
              </button>
            </div>

            <div className="report-modal-body">
              <div className="report-view-meta">
                <span><FiCalendar size={14} /> {viewReport.month || "N/A"} {viewReport.year || "N/A"}</span>
                <span><FiUser size={14} /> {viewReport.author || viewReport.createdBy?.userName || viewReport.createdBy?.name || "Unknown"}</span>
                <span className={`report-status-badge ${viewReport.status || "draft"}`}>{viewReport.status || "draft"}</span>
              </div>

              <div className="report-view-content">
                {viewReport.content ? (
                  viewReport.content.split("\n").map((line, i) => (
                    <p key={i}>{line || "\u00A0"}</p>
                  ))
                ) : (
                  <p style={{ color: "#8696a0" }}>No content available</p>
                )}
              </div>

              {viewReport.attachments?.length > 0 && (
                <div className="report-view-attachments">
                  <h4><FiPaperclip size={16} /> Attachments</h4>
                  <div className="report-view-files">
                    {viewReport.attachments.map((att, idx) => (
                      <div key={idx} className="report-view-file">
                        <FiFileText size={16} />
                        <span>{att.name}</span>
                        <span className="file-size">{att.size}</span>
                        <button
                          className="download-file-btn"
                          onClick={() => handleDownload(att)}
                        >
                          <FiDownload size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <FieldToast
        message={fieldToast.message}
        targetRef={fieldToast.targetRef}
        show={fieldToast.show}
      />
    </div>
  );
}